import { Controller, useForm } from 'react-hook-form';
import Select, { StylesConfig } from 'react-select';
import style from './SelectClinician.module.scss';
import { useEffect, useState } from 'react';
import {
  useDispatch,
  adminClinicianSearchChanged,
  adminClinicianSelectedRequested,
  useAppSelector,
  navigationSlice,
} from '@ax/redux-store';
import { useParams } from 'react-router-dom';
import { NavigationPages } from '@ax/constants';

interface ClinicianForm {
  clinicianID?: string;
}

interface Clinician {
  id?: string;
  guid?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface Option {
  value: Clinician;
  label: string;
}

export interface SelectClinicianProps {
  clinicianList: Clinician[];
  connectedClinician: Clinician;
}

const SelectClinician = () => {
  const clinicianList = useAppSelector((state) => state.admin.search.result);
  const connectedClinician = useAppSelector((state) => state.admin.connectedClinician);
  const selectedClinician = useAppSelector((state) => state.admin.selectedClinician);
  const dispatch = useDispatch();

  const formValues = { clinicianID: connectedClinician != null ? connectedClinician.id : '' };
  const { providerId } = useParams<{ providerId: string }>();

  const { control, watch, setValue } = useForm<ClinicianForm>({ defaultValues: formValues });

  const [clinicianOptions, setClinicianOptions] = useState<Option[]>([]);

  const convertData = () => {
    if (clinicianList?.data) {
      const clinicianOptions = clinicianList.data.map((item: Clinician) => ({
        value: item,
        label: item.firstName + ' ' + item.lastName,
      }));

      return [...clinicianOptions];
    }
    return [];
  };

  useEffect(() => {
    dispatch(adminClinicianSearchChanged({ inputValue: '' })); // to load clinician for the first time
  }, []);

  useEffect(() => {
    if (clinicianList) {
      setClinicianOptions(convertData());
    }
  }, [clinicianList, connectedClinician]);

  useEffect(() => {
    if (providerId && clinicianList?.data) {
      // TODO call onSelect with the clinician selected
      const clinician = clinicianList.data.find(
        (clinician: Clinician) => clinician.id == providerId,
      );
      if (clinician) {
        dispatch(adminClinicianSelectedRequested({ clinicianSelected: clinician }));
      }
    }
  }, [providerId, clinicianList]);

  useEffect(() => {
    if (selectedClinician) {
      const clinician = clinicianOptions.find((option) => option.value.id === selectedClinician.id);
      if (clinician) {
        setValue('clinicianID', clinician.value.id);
      }
    }
  }, [clinicianOptions, setValue, selectedClinician]);

  const onSelect = (clinician: any) => {
    // TODO: Check if we are in the /provider route, if not, just call navigationSlice.actions.pageNavigationRequested
    // TODO: If we are in the /provider route, call adminClinicianSelectedRequested

    dispatch(
      navigationSlice.actions.pageNavigationRequested({
        page: NavigationPages.connectedNotes,
        params: { providerId: clinician.value.id },
      }),
    );
  };

  const onTyping = (dataInput: string) => {
    dispatch(adminClinicianSearchChanged({ inputValue: dataInput }));
  };

  const customStyle: StylesConfig = {
    control: (provided: Record<string, unknown>, state: any) => ({
      ...provided,
      border: state.isFocused ? '1px solid #344bc6' : '1px solid #e0e0e0',
      boxShadow: state.isFocused ? '0px 0px 2px #344bc6' : 'none',
      '&:hover': {
        border: '1px solid #344bc6',
        boxShadow: '0px 0px 2px #344bc6',
      },
    }),
    option: (styles, state) => ({
      ...styles,
      backgroundColor: state.isSelected ? '#344bc6' : 'none',
    }),
  };

  return (
    <Controller
      name={'clinicianID'}
      control={control}
      defaultValue={formValues.clinicianID}
      render={({ field }) => (
        <Select
          {...field}
          name="clinicianID"
          options={clinicianOptions}
          onInputChange={(e) => onTyping(e)}
          onChange={(e) => onSelect(e)}
          value={clinicianOptions?.find((option) => option.value.id === watch('clinicianID'))}
          placeholder="Select a clinician..."
          classNames={{
            dropdownIndicator: () => {
              return style.custom_dropdown_indicator;
            },
          }}
          styles={customStyle}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: '#3173b1',
              neutral20: '#e0e0e0',
              neutral30: '#e0e0e0',
            },
          })}
        />
      )}
    />
  );
};

export default SelectClinician;
